import React, { useState } from 'react';
import { Calendar, Upload, Check, AlertCircle, Globe, Lock } from 'lucide-react';
import { parseEventText } from '../utils/eventParser';
import { useAuth } from '../contexts/AuthContext';
import { refreshGoogleToken, generateOAuthState } from '../utils/googleAuth';
import { toast } from '../utils/toast';
import { GOOGLE_API_CONFIG } from '../config/google';
import { supabase } from '../lib/supabase';
import CategorySelector from './settings/CategorySelector';

interface BulkCalendarUploadProps {
  onCalendarCreated: (calendarId: string) => void;
  onClose: () => void;
}

export default function BulkCalendarUpload({ onCalendarCreated, onClose }: BulkCalendarUploadProps) {
  const { user } = useAuth();
  const [calendarName, setCalendarName] = useState('');
  const [description, setDescription] = useState('');
  const [eventText, setEventText] = useState('');
  const [parsedEvents, setParsedEvents] = useState<any[]>([]);
  const [step, setStep] = useState<'input' | 'preview' | 'creating'>('input');
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const providerToken = localStorage.getItem('google_token');
  const isConnected = !!providerToken;

  const handleParse = () => {
    if (!eventText.trim()) {
      setError('Please enter event details');
      return;
    }

    if (!calendarName.trim()) {
      setError('Please enter a calendar name');
      return;
    }

    if (!categoryId) {
      setValidationErrors({ categoryId: 'Please select a category' });
      return;
    } else {
      setValidationErrors({});
    }

    try {
      const events = parseEventText(eventText);
      if (events.length === 0) {
        setError('No valid events found. Please check the format and try again.');
        return;
      }
      
      setParsedEvents(events);
      setStep('preview');
      setError(null);
    } catch (err) {
      console.error('Error parsing events:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse events');
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Store current path and modal state to return to
      sessionStorage.setItem('calendar_return_to', window.location.pathname);
      sessionStorage.setItem('open_calendar_modal', 'true');

      // Try to refresh token first if we have one
      if (providerToken) {
        const refreshed = await refreshGoogleToken();
        if (refreshed) {
          setIsConnecting(false);
          toast.success('Google Calendar reconnected successfully');
          return;
        }
      }

      // If no token or refresh failed, do full auth
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', GOOGLE_API_CONFIG.CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', `${window.location.origin}/google-callback.html`);
      authUrl.searchParams.append('response_type', 'token');
      authUrl.searchParams.append('scope', 'https://www.googleapis.com/auth/calendar');
      authUrl.searchParams.append('prompt', 'consent');
      authUrl.searchParams.append('state', generateOAuthState());

      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Google auth error:', err);
      setError('Failed to connect to Google Calendar');
      setIsConnecting(false);
    }
  };

  const createCalendarWithEvents = async () => {
    if (!isConnected || !user) {
      setError('Please connect your Google Calendar first');
      return;
    }

    setStep('creating');
    setError(null);

    try {
      // Create Google Calendar
      const calendar = await createGoogleCalendar();
      
      // Create events in the calendar
      await Promise.all(parsedEvents.map(event => 
        createGoogleEvent(calendar.id, event)
      ));

      // Create calendar in database
      const calendarId = await createDatabaseCalendar(calendar);
      
      toast.success('Calendar and events created successfully!');
      onCalendarCreated(calendarId);
    } catch (err) {
      console.error('Error creating calendar and events:', err);
      setError(err instanceof Error ? err.message : 'Failed to create calendar and events');
      setStep('preview');
    }
  };

  const createGoogleCalendar = async () => {
    try {
      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${providerToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          summary: calendarName,
          description: description,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      });

      if (!response.ok) {
        // Check if token expired
        if (response.status === 401) {
          // Try to refresh token
          const refreshed = await refreshGoogleToken();
          if (refreshed) {
            // Retry with new token
            return createGoogleCalendar();
          }
          throw new Error('Your Google Calendar connection has expired. Please reconnect.');
        }
        throw new Error('Failed to create Google Calendar');
      }

      const calendar = await response.json();

      // Make calendar public
      const sharingResponse = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${calendar.id}/acl`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${providerToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            role: 'reader',
            scope: {
              type: 'default'
            }
          })
        }
      );

      if (!sharingResponse.ok) {
        console.warn('Could not make calendar public automatically');
      }

      return calendar;
    } catch (err) {
      console.error('Error creating Google Calendar:', err);
      throw err;
    }
  };

  const createGoogleEvent = async (calendarId: string, event: any) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${providerToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            summary: event.title,
            description: event.description || '',
            start: {
              dateTime: event.start.toISOString(),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            end: {
              dateTime: event.end.toISOString(),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to create event: ${event.title}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Error creating Google Event:', err);
      throw err;
    }
  };

  const createDatabaseCalendar = async (googleCalendar: any) => {
    try {
      // Use Supabase client directly instead of fetch API
      const { data, error } = await supabase
        .from('calendars')
        .insert([
          {
            user_id: user.id,
            name: calendarName,
            description: description || null,
            google_calendar_url: `https://calendar.google.com/calendar/ical/${googleCalendar.id}/public/basic.ics`,
            is_public: isPublic,
            category_id: categoryId
          }
        ])
        .select()
        .single();

      if (error) throw error;
      return data.id;
    } catch (err) {
      console.error('Error creating calendar in database:', err);
      throw err;
    }
  };

  if (!isConnected) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Bulk Calendar Upload</h2>
          <p className="text-gray-600 mb-6">
            Connect your Google Calendar to create a new calendar with multiple events at once.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <button
          onClick={handleGoogleAuth}
          disabled={isConnecting}
          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
        >
          {isConnecting ? 'Connecting...' : 'Connect Google Calendar'}
        </button>
      </div>
    );
  }

  if (step === 'input') {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <Upload className="h-12 w-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Bulk Calendar Upload</h2>
          <p className="text-gray-600 mb-6">
            Create a new calendar with multiple events by pasting your event details below.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="calendarName" className="block text-sm font-medium text-gray-700 mb-1">
            Calendar Name*
          </label>
          <input
            id="calendarName"
            type="text"
            value={calendarName}
            onChange={(e) => setCalendarName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            placeholder="Enter calendar name"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-500">(optional)</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            placeholder="Brief description of your calendar"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category*
          </label>
          <CategorySelector
            selectedId={categoryId}
            onChange={setCategoryId}
            error={validationErrors.categoryId}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Calendar Visibility
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setIsPublic(true)}
              className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg ${
                isPublic
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Globe className="h-4 w-4 mr-2" />
              <span className="font-medium">Public</span>
            </button>
            <button
              type="button"
              onClick={() => setIsPublic(false)}
              className={`flex items-center justify-center px-4 py-3 border-2 rounded-lg ${
                !isPublic
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Lock className="h-4 w-4 mr-2" />
              <span className="font-medium">Private</span>
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {isPublic
              ? 'Anyone can find and subscribe to this calendar'
              : 'Only people you share the link with can subscribe'}
          </p>
        </div>

        <div>
          <label htmlFor="eventText" className="block text-sm font-medium text-gray-700 mb-1">
            Event Details*
          </label>
          <textarea
            id="eventText"
            value={eventText}
            onChange={(e) => setEventText(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            placeholder="Paste your event details here. Example:
Wed, July 9 – 10am MT (The Sales Process)
Wed, July 16 – 2pm MT (Behind the Build)
Wed, July 30 – 4pm MT (Modern AI Sales Pipeline)"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Each line should contain a date, time, and event title in parentheses.
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleParse}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (step === 'preview') {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Preview Events</h2>
          <p className="text-gray-600 mb-6">
            Review the events that will be created in your new calendar.
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-md mb-4">
          <h3 className="font-medium text-gray-900">{calendarName}</h3>
          {description && <p className="text-gray-600 text-sm mt-1">{description}</p>}
          <div className="mt-2 flex items-center">
            <span className={`px-2 py-1 text-xs rounded-full ${isPublic ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
              {isPublic ? 'Public' : 'Private'}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {parsedEvents.map((event, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-md p-4">
              <h4 className="font-medium text-gray-900">{event.title}</h4>
              <p className="text-gray-600 text-sm mt-1">
                {event.start.toLocaleString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short'
                })}
                {' to '}
                {event.end.toLocaleString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                  timeZoneName: 'short'
                })}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => setStep('input')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Back
          </button>
          <button
            type="button"
            onClick={createCalendarWithEvents}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md"
          >
            Create Calendar & Events
          </button>
        </div>
      </div>
    );
  }

  if (step === 'creating') {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Creating Your Calendar</h2>
          <p className="text-gray-600">
            Please wait while we create your calendar and events...
          </p>
        </div>
      </div>
    );
  }

  return null;
}